import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Tag, Percent } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { CashierNumericKeypad } from "./CashierNumericKeypad";
import type { CashierDiscountKind, CashierLabels, CashierOrderLineDiscount } from "./types";

export interface CashierDiscountSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (discount: CashierOrderLineDiscount) => void;
  labels: CashierLabels;
  lineLabel?: string;
  presets?: Array<{ key: string; label: string; kind: CashierDiscountKind; value: number; reasonCode?: string }>;
}

const DEFAULT_PRESETS = (labels: CashierLabels) => [
  { key: "loyalty10", label: labels.discount.presetLoyalty10, kind: "percent" as const, value: 10, reasonCode: "loyalty" },
  { key: "employee15", label: labels.discount.presetEmployee15, kind: "percent" as const, value: 15, reasonCode: "employee" },
];

export function CashierDiscountSheet({
  isOpen, onClose, onApply, labels, lineLabel, presets,
}: CashierDiscountSheetProps) {
  const [mode, setMode] = React.useState<"percent" | "amount">("percent");
  const [value, setValue] = React.useState("");
  const list = presets ?? DEFAULT_PRESETS(labels);

  React.useEffect(() => {
    if (isOpen) { setMode("percent"); setValue(""); }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/40"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-label={labels.discount.title}
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-x-0 bottom-0 z-50 bg-biz-bg rounded-t-[14px] biz-shadow-lg max-h-[90vh] overflow-auto"
          >
            <div className="mx-auto h-1.5 w-12 rounded-full bg-biz-border mt-3" />
            <div className="px-5 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-biz-foreground">{labels.discount.title}</h2>
                {lineLabel && <p className="text-xs text-biz-foreground-muted mt-0.5">{lineLabel}</p>}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="h-10 w-10 rounded-biz-btn text-biz-foreground-muted hover:bg-biz-muted biz-focus-ring transition-colors flex items-center justify-center"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-5 pb-5 max-w-2xl mx-auto space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {list.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => onApply({ kind: p.kind, valueMinor: p.value, reasonCode: p.reasonCode ?? null })}
                    className={cn(
                      "min-h-[64px] rounded-biz-btn border border-biz-success/30 bg-biz-success-subtle/60 text-biz-success",
                      "px-3 py-2 text-sm font-semibold flex flex-col items-start justify-center",
                      "hover:bg-biz-success-subtle transition-colors biz-focus-ring",
                    )}
                  >
                    <span className="flex items-center gap-1.5">
                      <Tag weight="fill" className="h-4 w-4" />
                      {p.label}
                    </span>
                  </button>
                ))}
              </div>

              <div className="rounded-biz-card border border-biz-border bg-biz-surface p-3 space-y-3">
                <div className="flex gap-2">
                  <ModeChip active={mode === "percent"} onClick={() => setMode("percent")}>
                    <Percent className="h-4 w-4" /> %
                  </ModeChip>
                  <ModeChip active={mode === "amount"} onClick={() => setMode("amount")}>
                    {labels.currencyPrefix}
                  </ModeChip>
                </div>
                <div className="text-right font-mono-num text-3xl font-bold text-biz-foreground tabular-nums px-3 py-2 bg-biz-bg rounded-biz-btn">
                  {value || "0"}{mode === "percent" ? "%" : ""}
                </div>
                <CashierNumericKeypad
                  value={value}
                  onChange={setValue}
                  onSubmit={(v) => {
                    const num = Number(v) || 0;
                    if (num <= 0) return;
                    onApply({
                      kind: mode === "percent" ? "percent" : "amount",
                      valueMinor: mode === "percent" ? num : num * 100,
                      reasonCode: "manual",
                    });
                  }}
                  mode={mode}
                  max={mode === "percent" ? 100 : undefined}
                  labels={labels}
                  submitLabel={labels.discount.apply}
                />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function ModeChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-11 px-4 rounded-biz-btn text-sm font-semibold inline-flex items-center gap-1.5 biz-focus-ring transition-colors",
        active
          ? "bg-biz-primary text-biz-primary-foreground"
          : "bg-biz-muted text-biz-foreground-muted hover:bg-biz-border",
      )}
    >
      {children}
    </button>
  );
}
